import { useFragment, useMutation } from '@apollo/client/react';
import {
  Loader2,
  ExternalLink,
  SquarePen,
  PlugZap,
  Cable,
  EllipsisVertical,
  EyeIcon,
  CableIcon,
  CogIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router';
import { Fragment } from 'react';

import { OauthIntegrationProviderEnum, type ICustomIntegration, type IOauthIntegration } from 'src/graphql/schema';
import { ToggleIntegrationDocument } from 'src/graphql/actions/integration/toggleIntegration.mutation';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from '@/components/ui/dropdown-menu';
import { ClientError, getApiError } from 'src/utils/errors';
import { cn } from 'src/lib/utils';
import { providerUrlSlugs } from 'src/shared/entities';
import {
  CustomIntegrationFragment,
  OauthIntegrationFragment,
} from 'src/graphql/actions/integration/integration.fragment';
import { Button } from '@/components/ui/button';

import { useIntegration, type IActiveIntegrationProps } from '../model';
import { refetchIntegrations } from '../api';

export const ReconnectCustomIntegration = ({ id }: { id: string }) => {
  const [toggleIntegration, { loading: loadingToggle }] = useMutation(ToggleIntegrationDocument, {
    onCompleted: refetchIntegrations,
  });

  const onReconnect = async (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    document.documentElement.style.cursor = 'progress';
    document.body.style.pointerEvents = 'none';

    e.preventDefault();

    try {
      const { data } = await toggleIntegration({ variables: { integrationId: id } });
      if (data?.toggleIntegration?.success) {
        toast.success('Integration connected');
      } else {
        throw new ClientError(data?.toggleIntegration?.errors?.[0]);
      }
    } catch (error) {
      getApiError(error, toast.error);
    } finally {
      document.documentElement.style.cursor = 'default';
      document.body.style.pointerEvents = 'auto';
    }
  };

  return (
    <DropdownMenuItem
      className={cn('py-2 cursor-pointer', {
        'pointer-events-none': loadingToggle,
      })}
      onClick={onReconnect}
    >
      {loadingToggle ? <Loader2 className="animate-spin" /> : <CableIcon />}
      Reconnect
    </DropdownMenuItem>
  );
};

export const ReconnectOauthIntegration = ({
  id,
  provider,
  tenantId,
}: Pick<IActiveIntegrationProps['integration'], 'id' | 'provider' | 'tenantId'>) => {
  const { reconnectOauthIntegration } = useIntegration();

  const onReconnect = () => reconnectOauthIntegration(provider as OauthIntegrationProviderEnum, tenantId ?? null, id);

  return (
    <DropdownMenuItem className="py-2 cursor-pointer" onClick={onReconnect}>
      <Cable /> Reconnect
    </DropdownMenuItem>
  );
};

export const IntegrationConnectionToggle = ({
  id,
  __typename,
}: Pick<IActiveIntegrationProps['integration'], 'id' | '__typename'>) => {
  const { disconnectIntegration } = useIntegration();
  const { data: integration } = useFragment<ICustomIntegration | IOauthIntegration>({
    fragment: __typename === 'CustomIntegration' ? CustomIntegrationFragment : OauthIntegrationFragment,
    from: {
      __typename,
      id,
    },
  });

  if (!integration || !integration?.policy?.update || __typename === 'ShareFileIntegration') return null;

  const { active, provider } = integration as ICustomIntegration | IOauthIntegration;

  const onDisconnect = () => disconnectIntegration(id);

  if (active)
    return (
      <DropdownMenuItem className="py-2 cursor-pointer text-destructive focus:text-destructive!" onClick={onDisconnect}>
        <PlugZap />
        Disconnect
      </DropdownMenuItem>
    );

  if (__typename === 'CustomIntegration') return <ReconnectCustomIntegration id={id} />;

  if (__typename === 'OauthIntegration')
    return (
      <ReconnectOauthIntegration id={id} provider={provider} tenantId={(integration as IOauthIntegration).tenantId} />
    );
};

export const UpdateCredentialsCustomIntegration = ({
  id,
  __typename,
}: Pick<IActiveIntegrationProps['integration'], 'id' | '__typename'>) => {
  const { updateCustomIntegration } = useIntegration();
  const { data: integration } = useFragment<ICustomIntegration>({
    fragment: CustomIntegrationFragment,
    from: {
      __typename,
      id,
    },
  });

  if (__typename !== 'CustomIntegration' || !integration?.policy?.update || !integration?.active) return null;

  const onUpdateCredentials = () => updateCustomIntegration(id);

  return (
    <DropdownMenuItem className="py-2 cursor-pointer" onClick={onUpdateCredentials}>
      <SquarePen />
      Update credentials
    </DropdownMenuItem>
  );
};

const IntegrationDetails = ({
  id,
  canManage,
  canRead,
  __typename,
}: Pick<IActiveIntegrationProps['integration'], 'id' | '__typename'> & {
  canManage: boolean;
  canRead: boolean;
}) => {
  const navigate = useNavigate();

  const onViewDetails = () => navigate(`/integrations/${id}`);

  if (canRead)
    return (
      <DropdownMenuItem className="py-2 cursor-pointer" onClick={onViewDetails}>
        {canManage && __typename !== 'ShareFileIntegration' ? (
          <Fragment>
            <CogIcon />
            Manage integration
          </Fragment>
        ) : (
          <Fragment>
            <EyeIcon />
            View details
          </Fragment>
        )}
      </DropdownMenuItem>
    );

  return null;
};

const IntegrationDropdownMenu = ({
  id,
  provider,
  __typename: type,
  canManage,
  canRead,
}: Pick<IActiveIntegrationProps['integration'], 'id' | 'provider' | '__typename'> & {
  canManage: boolean;
  canRead: boolean;
}) => {
  const onMoreInfoClick = () => {
    const path = providerUrlSlugs[provider as keyof typeof providerUrlSlugs];

    window.open(`https://codekeeper.co/integrations/${path}`, '_blank');
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon">
          <EllipsisVertical />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="bottom" align="end" className="w-max">
        <IntegrationDetails id={id} __typename={type} canManage={canManage} canRead={canRead} />
        <DropdownMenuItem className="py-2 cursor-pointer" onClick={onMoreInfoClick}>
          <ExternalLink />
          Learn more
        </DropdownMenuItem>
        <UpdateCredentialsCustomIntegration id={id} __typename={type} />
        <IntegrationConnectionToggle id={id} __typename={type} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default IntegrationDropdownMenu;
